import { ProviderId } from "@indeed/signals-schema/com/indeed/signals/schema/reactnative/ProviderId";
import { useMemo } from "react";

import { useOptionalReportError } from "../components/ReportErrorProvider/ReportErrorContext";
import { getErrorMessage, getErrorStackTrace } from "../logging/DatadogErrorUtils";
import { JSTReport, type JSTReport as JSTReportPayload } from "../native-modules/tare/JSTReport";
import { report } from "../native-modules/tare/report";
import { getRnErrorProviderId } from "../util/getRnErrorProviderId";

export interface JSTReporter {
  info(message: string, options?: ReportOptions): void;
  warn(message: string, options?: ReportOptions): void;
  error(errorOrMessage: unknown, options?: ReportOptions): void;
}

export interface ReportOptions {
  errDomain?: string;
  errCode?: number;
  url?: string;
  statusCode?: number;
  context?: Record<string, string | number>;
  containsPII?: boolean;
}

type ConsoleMethod = (...params: unknown[]) => void;

interface PristineConsole {
  info: ConsoleMethod;
  warn: ConsoleMethod;
  error: ConsoleMethod;
}

let pristineConsole: PristineConsole | undefined;

function getPristineConsole(): PristineConsole {
  // Web entry points have no global error interceptor or startup initialization.
  return (pristineConsole ??= {
    info: console.info,
    warn: console.warn,
    error: console.error,
  });
}

function writeReport(
  providerId: string,
  createPayload: () => JSTReportPayload,
  writeConsole: () => void,
): void {
  try {
    const payload = createPayload();
    void report({
      ...payload,
      // Preserve feature attribution independently of errDomain (JSMA-5222).
      context: { ...payload.context, providerId },
    }).catch(() => {
      // Console diagnostics can enter the legacy error handler or LogBox.
      // A reporting failure must terminate here.
    });
  } catch {
    // Telemetry extraction must not prevent forwarding the original arguments.
  }
  writeConsole();
}

// For modules outside a ReportErrorProvider, such as utilities, Apollo links
// and native-module wrappers. Components should use useJSTReporter().
export function getJSTReporter(providerName: string): JSTReporter {
  const providerId = getRnErrorProviderId(providerName);
  return {
    info: (message, options) => {
      writeReport(
        providerName,
        () => JSTReport.info(providerId, message, options),
        () => getPristineConsole().info.call(console, message),
      );
    },
    warn: (message, options) => {
      writeReport(
        providerName,
        () => JSTReport.warn(providerId, message, options),
        () => getPristineConsole().warn.call(console, message),
      );
    },
    error: (errorOrMessage, options) => {
      writeReport(
        providerName,
        () =>
          JSTReport.error(providerId, getErrorMessage(errorOrMessage), {
            ...options,
            stack: getErrorStackTrace(errorOrMessage) || undefined,
          }),
        () => getPristineConsole().error.call(console, errorOrMessage),
      );
    },
  };
}

// Call before initializeGlobalErrorHandler on native so reporter-local output
// bypasses the legacy console.error interceptor without replacing any globals.
export function initializeJSTReporter(): void {
  getPristineConsole();
}

export function resetJSTReporter(): void {
  pristineConsole = undefined;
}

// Shared hooks and components can render outside a ReportErrorProvider
// (web entry points, tests), so those reports fall back to Unknown.
export function useJSTReporter(): JSTReporter {
  const providerId = useOptionalReportError()?.providerId ?? ProviderId.Unknown;

  return useMemo<JSTReporter>(() => getJSTReporter(providerId), [providerId]);
}
